import { database } from './firebase';
import { ref, set, get, update, onValue } from 'firebase/database';

/**
 * Settings Service
 * Manages user preferences and settings in Firebase
 */

// Default settings structure
const DEFAULT_SETTINGS = {
  theme: 'light', // 'light' or 'dark'
  notifications: {
    sound: true,
    desktop: true,
    friendRequests: true,
    gameInvites: true,
    messages: true,
    gameEvents: true,
  },
  privacy: {
    showOnlineStatus: true,
    allowFriendRequests: true,
    showGameHistory: true,
  },
  gameplay: {
    autoRematch: false,
    showHints: true,
    animationSpeed: 'normal', // 'slow', 'normal', 'fast'
    soundEffects: true,
    backgroundMusic: false,
  },
  display: {
    language: 'en',
    timeFormat: '12h', // '12h' or '24h'
    timezone: 'America/Chicago',
    reducedMotion: false,
  },
};

/**
 * Get user settings from Firebase
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User settings
 */
export const getUserSettings = async (userId) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}`);
    const snapshot = await get(settingsRef);

    if (snapshot.exists()) {
      // Merge with defaults to ensure all settings exist
      return { ...DEFAULT_SETTINGS, ...snapshot.val() };
    } else {
      // Create default settings for new user
      await set(settingsRef, DEFAULT_SETTINGS);
      return DEFAULT_SETTINGS;
    }
  } catch (error) {
    console.error('Error getting user settings:', error);
    return DEFAULT_SETTINGS;
  }
};

/**
 * Update user settings in Firebase
 * @param {string} userId - User ID
 * @param {Object} settings - Settings to update (partial or full)
 * @returns {Promise<boolean>} Success status
 */
export const updateUserSettings = async (userId, settings) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}`);
    await update(settingsRef, settings);
    return true;
  } catch (error) {
    console.error('Error updating user settings:', error);
    return false;
  }
};

/**
 * Update theme preference
 * @param {string} userId - User ID
 * @param {string} theme - Theme name ('light' or 'dark')
 * @returns {Promise<boolean>} Success status
 */
export const updateTheme = async (userId, theme) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}/theme`);
    await set(settingsRef, theme);

    // Apply theme to document
    applyTheme(theme);

    return true;
  } catch (error) {
    console.error('Error updating theme:', error);
    return false;
  }
};

/**
 * Apply theme to document
 * @param {string} theme - Theme name
 */
export const applyTheme = (theme) => {
  document.documentElement.setAttribute('data-theme', theme);
  localStorage.setItem('theme', theme);
};

/**
 * Get current theme from localStorage or system preference
 * @returns {string} Current theme
 */
export const getCurrentTheme = () => {
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme) return savedTheme;

  // Check system preference
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }

  return 'light';
};

/**
 * Update notification preferences
 * @param {string} userId - User ID
 * @param {Object} notificationSettings - Notification preferences
 * @returns {Promise<boolean>} Success status
 */
export const updateNotificationSettings = async (userId, notificationSettings) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}/notifications`);
    await update(settingsRef, notificationSettings);
    return true;
  } catch (error) {
    console.error('Error updating notification settings:', error);
    return false;
  }
};

/**
 * Update privacy settings
 * @param {string} userId - User ID
 * @param {Object} privacySettings - Privacy preferences
 * @returns {Promise<boolean>} Success status
 */
export const updatePrivacySettings = async (userId, privacySettings) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}/privacy`);
    await update(settingsRef, privacySettings);
    return true;
  } catch (error) {
    console.error('Error updating privacy settings:', error);
    return false;
  }
};

/**
 * Update gameplay settings
 * @param {string} userId - User ID
 * @param {Object} gameplaySettings - Gameplay preferences
 * @returns {Promise<boolean>} Success status
 */
export const updateGameplaySettings = async (userId, gameplaySettings) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}/gameplay`);
    await update(settingsRef, gameplaySettings);
    return true;
  } catch (error) {
    console.error('Error updating gameplay settings:', error);
    return false;
  }
};

/**
 * Update display settings
 * @param {string} userId - User ID
 * @param {Object} displaySettings - Display preferences
 * @returns {Promise<boolean>} Success status
 */
export const updateDisplaySettings = async (userId, displaySettings) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}/display`);
    await update(settingsRef, displaySettings);

    // Apply reduced motion preference
    if (displaySettings.reducedMotion !== undefined) {
      document.documentElement.style.setProperty(
        '--animation-duration',
        displaySettings.reducedMotion ? '0.01ms' : '0.3s'
      );
    }

    return true;
  } catch (error) {
    console.error('Error updating display settings:', error);
    return false;
  }
};

/**
 * Subscribe to settings changes
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function to handle settings updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToSettings = (userId, callback) => {
  const settingsRef = ref(database, `userSettings/${userId}`);

  const unsubscribe = onValue(settingsRef, (snapshot) => {
    if (snapshot.exists()) {
      const settings = { ...DEFAULT_SETTINGS, ...snapshot.val() };
      callback(settings);
    } else {
      callback(DEFAULT_SETTINGS);
    }
  });

  return unsubscribe;
};

/**
 * Reset settings to default
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Success status
 */
export const resetSettings = async (userId) => {
  try {
    const settingsRef = ref(database, `userSettings/${userId}`);
    await set(settingsRef, DEFAULT_SETTINGS);

    // Apply default theme
    applyTheme(DEFAULT_SETTINGS.theme);

    return true;
  } catch (error) {
    console.error('Error resetting settings:', error);
    return false;
  }
};

/**
 * Export user settings
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Settings object or null
 */
export const exportSettings = async (userId) => {
  try {
    const settings = await getUserSettings(userId);

    // Create downloadable JSON
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    return { settings, url };
  } catch (error) {
    console.error('Error exporting settings:', error);
    return null;
  }
};

/**
 * Import user settings
 * @param {string} userId - User ID
 * @param {Object} settings - Settings to import
 * @returns {Promise<boolean>} Success status
 */
export const importSettings = async (userId, settings) => {
  try {
    // Validate settings structure
    const validatedSettings = { ...DEFAULT_SETTINGS, ...settings };

    const settingsRef = ref(database, `userSettings/${userId}`);
    await set(settingsRef, validatedSettings);

    // Apply theme
    applyTheme(validatedSettings.theme);

    return true;
  } catch (error) {
    console.error('Error importing settings:', error);
    return false;
  }
};
